import { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IntegrationConnectorType, IntegrationPublic } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState } from '@/components/StateMessage/StateMessage';
import { useToast } from '@/components/toast/ToastProvider';
import { IntegrationConnectionCard } from '@/features/integrations/IntegrationConnectionCard';
import { IntegrationSetupWizard } from '@/features/integrations/IntegrationSetupWizard';
import { ServiceAccountUpload } from '@/features/integrations/ServiceAccountUpload';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { ProjectPickerEmpty } from '@/features/projects/ProjectPickerEmpty';
import { ProjectWorkspaceHeader } from '@/features/projects/ProjectWorkspaceHeader';
import { useProjectContext } from '@/project/ProjectContext';
import styles from './IntegrationsPage.module.css';

export function IntegrationsPage() {
  const { selectedProject, selectedProjectId, setSelectedProjectId } = useProjectContext();
  const projectId = selectedProject?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [connectorType, setConnectorType] = useState<IntegrationConnectorType>('ga4');
  const [credentials, setCredentials] = useState('');
  const [credentialFileName, setCredentialFileName] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [siteUrl, setSiteUrl] = useState('');

  const listQ = useQuery({
    queryKey: ['integrations', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ integrations: IntegrationPublic[] }>(
        `/api/integrations?project_id=${encodeURIComponent(projectId!)}`,
      ),
  });

  const brevoOAuthQ = useQuery({
    queryKey: ['brevo-oauth-status'],
    queryFn: () => apiFetch<{ oauth_available: boolean }>('/api/integrations/brevo/oauth/status'),
  });

  const integrations = listQ.data?.integrations ?? [];
  const apiOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  const stats = useMemo(() => {
    return {
      total: integrations.length,
      active: integrations.filter((i) => i.status === 'active').length,
      error: integrations.filter((i) => i.status === 'error').length,
    };
  }, [integrations]);

  const startBrevoOAuth = useMutation({
    mutationFn: () =>
      apiFetch<{ authorize_url: string }>('/api/integrations/brevo/oauth/start', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId }),
      }),
    onSuccess: (data) => {
      window.location.href = data.authorize_url;
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['integrations', projectId] });

  const scheduleRefresh = () => {
    window.setTimeout(invalidate, 4000);
    window.setTimeout(invalidate, 12000);
  };

  const createIntegration = useMutation({
    mutationFn: () => {
      if (connectorType === 'ga4' && !propertyId.trim()) {
        throw new Error('GA4 property ID is verplicht');
      }
      if (connectorType === 'gsc' && !siteUrl.trim()) {
        throw new Error('GSC site URL is verplicht');
      }
      if (connectorType === 'wordpress' && !siteUrl.trim()) {
        throw new Error('WordPress site URL is verplicht');
      }
      const config: Record<string, unknown> = {};
      if (connectorType === 'ga4' && propertyId.trim()) config.property_id = propertyId.trim();
      if (connectorType === 'gsc' && siteUrl.trim()) config.site_url = siteUrl.trim();
      if (connectorType === 'wordpress' && siteUrl.trim()) config.site_url = siteUrl.trim();
      if (connectorType === 'brevo' && propertyId.trim()) {
        config.default_sender_email = propertyId.trim();
      }
      if (!credentials.trim()) throw new Error('Credentials zijn verplicht');
      return apiFetch<{ integration: IntegrationPublic; sync_queued?: boolean }>('/api/integrations', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          connector_type: connectorType,
          config,
          credentials,
        }),
      });
    },
    onSuccess: (data) => {
      invalidate();
      setWizardOpen(false);
      setCredentials('');
      setCredentialFileName(null);
      setPropertyId('');
      setSiteUrl('');
      if (data.integration?.id) setExpandedId(data.integration.id);
      scheduleRefresh();
      toast.push(data.sync_queued ? 'Koppeling actief — sync gestart' : 'Koppeling opgeslagen', 'success');
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const patchConfig = useMutation({
    mutationFn: ({ id, config }: { id: string; config: Record<string, unknown> }) =>
      apiFetch(`/api/integrations/${id}`, { method: 'PATCH', body: JSON.stringify({ config }) }),
    onSuccess: () => {
      invalidate();
      scheduleRefresh();
      toast.push('Opgeslagen', 'success');
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const testIntegration = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/integrations/${id}/test`, { method: 'POST', body: '{}' }),
    onSuccess: (data: { message?: string; sync_queued?: boolean }) => {
      invalidate();
      toast.push(data.message ?? 'Credentials OK', 'success');
      if (data.sync_queued) scheduleRefresh();
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const syncIntegration = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/integrations/${id}/sync`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      invalidate();
      scheduleRefresh();
      toast.push('Sync in wachtrij', 'success');
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const deleteIntegration = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/integrations/${id}`, { method: 'DELETE', body: '{}' }),
    onSuccess: (_, id) => {
      invalidate();
      if (expandedId === id) setExpandedId(null);
      toast.push('Koppeling verwijderd', 'success');
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const busy =
    createIntegration.isPending ||
    testIntegration.isPending ||
    syncIntegration.isPending ||
    deleteIntegration.isPending ||
    patchConfig.isPending;

  const wizardStepCount =
    connectorType === 'ga4' ||
    connectorType === 'gsc' ||
    connectorType === 'brevo' ||
    connectorType === 'wordpress'
      ? 3
      : 2;

  const configStep =
    connectorType === 'brevo' ? (
      <Field compact label="Standaard afzender (optioneel)" hint="Verified sender in Brevo">
        <TextInput
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          placeholder="support@example.com"
        />
      </Field>
    ) : connectorType === 'ga4' ? (
      <Field compact label="GA4 property ID" hint="Admin → Property settings">
        <TextInput
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          placeholder="123456789"
        />
      </Field>
    ) : connectorType === 'gsc' ? (
      <Field compact label="Site URL" hint="Exact zoals in Search Console">
        <TextInput
          value={siteUrl}
          onChange={(e) => setSiteUrl(e.target.value)}
          placeholder="https://www.example.com/"
        />
      </Field>
    ) : connectorType === 'wordpress' ? (
      <Field compact label="WordPress site URL">
        <TextInput
          value={siteUrl}
          onChange={(e) => setSiteUrl(e.target.value)}
          placeholder="https://ploko.nl"
        />
      </Field>
    ) : null;

  const credentialsStep =
    connectorType === 'brevo' ? (
      <>
        {brevoOAuthQ.data?.oauth_available ? (
          <Button
            type="button"
            variant="secondary"
            disabled={!projectId || startBrevoOAuth.isPending}
            onClick={() => startBrevoOAuth.mutate()}
          >
            Verbinden via Brevo OAuth
          </Button>
        ) : null}
        <Field compact label="Brevo API key" hint="Transactional + Webhooks">
          <TextInput
            type="password"
            autoComplete="off"
            value={credentials}
            onChange={(e) => setCredentials(e.target.value)}
            placeholder="xkeysib-…"
          />
        </Field>
      </>
    ) : connectorType === 'wordpress' ? (
      <Field
        compact
        label="Application Password"
        hint='JSON {"username","application_password"}'
      >
        <TextArea
          rows={4}
          autoComplete="off"
          value={credentials}
          onChange={(e) => setCredentials(e.target.value)}
        />
      </Field>
    ) : (
      <ServiceAccountUpload
        loadedName={credentialFileName}
        loadedSize={credentials ? credentials.length : null}
        onLoad={(text, name) => {
          setCredentials(text);
          setCredentialFileName(name);
        }}
        onClear={() => {
          setCredentials('');
          setCredentialFileName(null);
        }}
      />
    );

  function submitWizard(e: FormEvent) {
    e.preventDefault();
    createIntegration.mutate();
  }

  return (
    <ProjectGate pageTitle="Integraties">
      {!selectedProjectId ? (
        <ProjectPickerEmpty
          title="Integraties"
          description="Koppel analytics, mail en CMS per project. Kies eerst een werkruimte."
          cardHint="Integraties voor dit project"
        />
      ) : listQ.isLoading ? (
        <LoadingState />
      ) : (
        <div className={styles.page}>
          <ProjectWorkspaceHeader
            title="Integraties"
            subtitle={
              <>
                Beheer data-koppelingen voor {selectedProject?.name}. Credentials zijn versleuteld.
                API-keys en webhooks: <Link to="/app/developer">Developer portal</Link>.
              </>
            }
            projectName={selectedProject?.name ?? ''}
            onSwitchProject={() => setSelectedProjectId(null)}
            actions={
              <Button type="button" onClick={() => setWizardOpen(true)}>
                Koppeling toevoegen
              </Button>
            }
          />

          <div className={styles.statsBar}>
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.total}</span>
              <span className={styles.statLabel}>Gekoppeld</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.active}</span>
              <span className={styles.statLabel}>Actief</span>
            </div>
            <div className={styles.stat}>
              <span className={`${styles.statValue} ${stats.error ? styles.statValueError : ''}`}>
                {stats.error}
              </span>
              <span className={styles.statLabel}>Fout</span>
            </div>
          </div>

          <section className={styles.connectionsPanel}>
            {!integrations.length ? (
              <div className={styles.emptyState}>
                <h2 className={styles.emptyTitle}>Nog geen koppelingen</h2>
                <p className={styles.emptyText}>
                  Voeg GA4, Search Console, Brevo of WordPress toe om data en leads te centraliseren.
                </p>
                <Button type="button" onClick={() => setWizardOpen(true)}>
                  Eerste koppeling maken
                </Button>
              </div>
            ) : (
              <ul className={styles.connectionList}>
                {integrations.map((i) => (
                  <li key={i.id}>
                    <IntegrationConnectionCard
                      integration={i}
                      expanded={expandedId === i.id}
                      onToggle={() => setExpandedId((cur) => (cur === i.id ? null : i.id))}
                      busy={busy}
                      onTest={() => testIntegration.mutate(i.id)}
                      onSync={() => syncIntegration.mutate(i.id)}
                      onDelete={() => deleteIntegration.mutate(i.id)}
                      onSaveConfig={(config) => patchConfig.mutate({ id: i.id, config })}
                      projectSlug={selectedProject?.slug}
                      apiOrigin={apiOrigin}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <IntegrationSetupWizard
            open={wizardOpen}
            onClose={() => setWizardOpen(false)}
            connectorType={connectorType}
            onConnectorTypeChange={setConnectorType}
            stepCount={wizardStepCount}
            configStep={configStep}
            credentialsStep={credentialsStep}
            notice={null}
            error={
              createIntegration.isError ? (
                <ErrorState message={(createIntegration.error as Error).message} />
              ) : null
            }
            submitting={createIntegration.isPending}
            canSubmit={Boolean(credentials.trim())}
            onSubmit={submitWizard}
          />
        </div>
      )}
    </ProjectGate>
  );
}
